import type { AuthProvider } from "ra-core";
import { HttpError } from "ra-core";

import type { Sale } from "../../types";
import { canAccess } from "../commons/canAccess";
import { API_URL, httpClient } from "./httpClient";
import {
  clearSession,
  getStoredUser,
  getToken,
  setStoredUser,
  setToken,
} from "./tokenStorage";

/** i18n keys; the login page translates the message of the rejection. */
export const LOGIN_ERROR_WRONG_CREDENTIALS = "crm.auth.wrong_credentials";

type LoginResponse = { token?: string; user?: Sale };

const fullName = (user: Sale | null): string =>
  user ? [user.first_name, user.last_name].filter(Boolean).join(" ") : "";

/**
 * Authentication against the PHP API.
 *
 * The bearer token and the logged-in user are kept in localStorage (see
 * `tokenStorage.ts`); `getIdentity` refreshes the user via `GET /auth/me`
 * when only a token survived a reload.
 */
export const createAuthProvider = (apiUrl: string = API_URL): AuthProvider => ({
  login: async ({ email, password }: { email: string; password: string }) => {
    clearSession();
    let response: LoginResponse;
    try {
      const { json } = await httpClient(`${apiUrl}/auth/login`, {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      response = (json ?? {}) as LoginResponse;
    } catch (error) {
      // The API answers with a Dutch `{ message }`; show it as-is and fall
      // back to a translated key when the request never reached the server.
      const message =
        error instanceof HttpError && error.message
          ? error.message
          : LOGIN_ERROR_WRONG_CREDENTIALS;
      throw new Error(message);
    }
    if (!response.token || !response.user) {
      throw new Error(LOGIN_ERROR_WRONG_CREDENTIALS);
    }
    setToken(response.token);
    setStoredUser(response.user);
  },

  logout: async () => {
    if (getToken()) {
      try {
        await httpClient(`${apiUrl}/auth/logout`, { method: "POST" });
      } catch {
        // The token is dropped locally anyway.
      }
    }
    clearSession();
  },

  checkAuth: async () => {
    if (!getToken()) {
      throw new Error();
    }
  },

  checkError: async (error: unknown) => {
    const status =
      error instanceof HttpError
        ? error.status
        : (error as { status?: number } | null)?.status;
    if (status === 401) {
      clearSession();
      // `message: false` keeps ra-core from notifying twice.
      return Promise.reject({ message: false });
    }
  },

  getIdentity: async () => {
    let user = getStoredUser();
    if (!user && getToken()) {
      const { json } = await httpClient(`${apiUrl}/auth/me`);
      user = (json ?? null) as Sale | null;
      if (user) {
        setStoredUser(user);
      }
    }
    if (!user) {
      throw new Error();
    }
    return { id: user.id, fullName: fullName(user) };
  },

  canAccess: async ({ signal: _signal, ...params }) => {
    const user = getStoredUser();
    if (!user) {
      return false;
    }
    return canAccess(user.administrator ? "admin" : "user", params, user.id);
  },
});

export const authProvider: AuthProvider = createAuthProvider();
